/**
 * Socket.IO connection to the /pairing namespace (QR device login).
 *
 * Independent of the player socket singleton in socket.ts: TV on /login and
 * phone on /pair/:code are not players in any session.
 */

import { io } from 'socket.io-client'
import { config } from '../config'

export interface PairingSocketHandlers {
  /** Called on every (re)connection to the pairing room. */
  onConnected: () => void
  onApproved: () => void
  onSessionReady: (sessionCode: string) => void
}

// TV remembers its pairing until it creates a session (HostPage reports it).
const PAIRING_STORAGE_KEY = 'giermek-device-pairing'
const PAIRING_STORAGE_TTL_MS = 600_000

interface StoredPairing {
  pairing_code: string
  expires_at: number
}

export function storePairing(pairingCode: string): void {
  const value: StoredPairing = {
    pairing_code: pairingCode,
    expires_at: Date.now() + PAIRING_STORAGE_TTL_MS,
  }
  sessionStorage.setItem(PAIRING_STORAGE_KEY, JSON.stringify(value))
}

export function clearStoredPairing(): void {
  sessionStorage.removeItem(PAIRING_STORAGE_KEY)
}

/** Removes the stored pairing and returns its code if still valid. */
export function takeStoredPairing(): string | null {
  const pairingCode = readStoredPairing()
  clearStoredPairing()
  return pairingCode
}

/** A valid pairing is stored (TV logged in by QR, session not created yet). */
export function hasStoredPairing(): boolean {
  return readStoredPairing() !== null
}

function readStoredPairing(): string | null {
  const raw = sessionStorage.getItem(PAIRING_STORAGE_KEY)
  if (raw === null) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (
    typeof parsed !== 'object' || parsed === null
    || !('pairing_code' in parsed) || typeof parsed.pairing_code !== 'string'
    || !('expires_at' in parsed) || typeof parsed.expires_at !== 'number'
  ) {
    return null
  }
  return parsed.expires_at > Date.now() ? parsed.pairing_code : null
}

/** Opens the pairing socket; returns a function that disconnects it. */
export function connectPairingSocket(
  pairingCode: string,
  handlers: PairingSocketHandlers,
): () => void {
  const socket = io(`${config.wsUrl}/pairing`, {
    auth: { pairing_code: pairingCode },
    // Own Manager, not shared with the player socket
    forceNew: true,
    transports: ['websocket', 'polling'],
  })

  socket.on('connect', () => handlers.onConnected())
  socket.on('pairing:approved', () => handlers.onApproved())
  socket.on('pairing:session_ready', (data: { session_code: string }) => {
    handlers.onSessionReady(data.session_code)
  })

  return () => {
    socket.removeAllListeners()
    socket.disconnect()
  }
}
